import {l} from "types/typescriptdefinisjoner_lederplan";
import {Kontekst} from "./Kontekst";
import {AutentiseringsRessurs} from "./ressurser/AutentiseringsRessurs";
import {IHttpService, IIntervalService, IPromise, IQService, IScope, ITimeoutService} from "angular";
import {LokaltDatalager} from "./LokaltDatalager";
import {regNavn} from "./tredjepartsTjenestenavn";
import {konverterIso8601DatoStrengTilDate} from "../infrastruktur/Iso8601DatostrengTilDate";
import {Debugger} from 'debug'
import {Konfigurasjon} from "../infrastruktur/Konfigurasjon";
import {MeldingTilBruker} from "./MeldingTilBruker";
import {gettextCatalog} from "angular-gettext";
import {CriiptoOIDCRessurs} from "./ressurser/CriiptoOIDCRessurs";
import {InterWindowBus} from "./InterWindowBus";
import moment from "moment";
import {erIkkeNill} from "felles/stoettefunk/nillsjekk";
import {LokallagretAutentisertModel} from "felles/tjenester/LokallagretAutentisertModel";

const debug:Debugger = require('debug')('Autentisering');

export class Autentisering {

    static regNavn = "Autentisering";
    /** Event som trigges når bruker er autentisert mot server, enten pga ny innlogging eller fordi eksisterende token har blitt validert mot server */
    static brukerAutentisertEvent = "Autentisering:brukerAutentisert";
    static tidligererInnloggetBrukerLastetEvent = "Autentisering:tidligererInnloggetBrukerLastet";
    static brukerLoggetInnEvent = "Autentisering:brukerLoggetInn";
    static brukerLoggetUtEvent = "Autentisering:brukerLoggetUt";
    static tokenValideringFeiletEvent = "Autentisering:tokenValideringFeilet";

    //eventer som sender over InterWindowBus
    static fornyKortTokenEvent = 'Autentisering:fornyKortToken';

    static forInjisering() {
        return [
            AutentiseringsRessurs.regNavn,
            CriiptoOIDCRessurs.regNavn,
            Kontekst.regNavn,
            Konfigurasjon.regNavn,
            InterWindowBus.regNavn,
            MeldingTilBruker.regNavn,
            regNavn.gettext.gettextCatalog,
            regNavn.ng.$q,
            regNavn.ng.$rootScope,
            regNavn.ng.$interval,
            regNavn.ng.$timeout,
            LokallagretAutentisertModel.regNavn,
            Autentisering
        ]
    }

    constructor(private autentiseringsRes: AutentiseringsRessurs,
                private criiptoOIDCRessurs: CriiptoOIDCRessurs,
                private kontekst: Kontekst,
                private konfigurasjon: Konfigurasjon,
                private interWindowBus: InterWindowBus,
                private meldingTilBruker:MeldingTilBruker,
                private gettextCatalog: gettextCatalog,
                private $q: IQService,
                private $rootScope: IScope,
                private $interval: IIntervalService,
                private $timeout: ITimeoutService,
                private lokallagretAutentisertModel: LokallagretAutentisertModel) {
        this.lyttTilFornyKortTokenHendelseIAnnenFane();
    }

    /** Benyttes av {@link #startAutomatiskKortTokenfornyelse() }*/
    private automatiskTokenfornyelseTimer;

    /** Benyttes av {@link #håndterFornyKortTokenStartHendelse() }*/
    private fornyKortTokenHendelseTimeout:IPromise<any> = null;

    private fornyKortTokenInitiertIAnnenFane =false;

    erAutentisert(): boolean {
        return this.kontekst.erAutentisert();
    }

    /**
     * Autentiserer bruker mot server med brukernavn/passord og evnt otp.
     * Oppdaterer kontekst med brukerdata returnert av server,
     * fyrer brukerLoggetInnEvent.
     *
     * Feilet autentisering må håndteres via IPromise.catch callback.
     * Kaller er ansvarlig for å identifisere nødvendig oppgradering av autentiseringsmetode ved å
     * inspisere respons {@link l.AutentiseringResponsModell} argumentet gitt til IPromise.catch callback.
     */
    autentiser(spesifikasjon: l.InnloggingsSpesifikasjon): IPromise<l.AutentisertOkModell> {
        const defered = this.$q.defer<l.AutentisertOkModell>();
        this.autentiseringsRes.autentiserv2(spesifikasjon).$promise
            .then((respons) => {
                if (respons.status !== l.AutentiseringStatus.autentisert) {
                    debug('bnp autentisering feilet', respons);
                    defered.reject(respons);
                    return;
                }

                debug('bnp autentisert - bruker funnet', respons);
                this.brukerAutentisertMotServer(respons.autentisertBruker);
                defered.resolve(respons.autentisertBruker)
            })
            //ved ikke-200 respons
            .catch(e => defered.reject(this.requstErrorSomAutentiseringResponsModell(e)));

        return defered.promise;
    }

    /**
     * Autentiserer bruker mot server med totp reservekode.
     * Oppdaterer kontekst med brukerdata returnert av server,
     * fyrer brukerLoggetInnEvent.
     *
     * Resultatet av autentiseringen håndteres via callbackmetodene.
     */
    autentiserMedTotpReservekode(spesifikasjon: l.TotpReservekodeInnloggingsSpesifikasjon, vedAutentiseringOk: (autentisert: l.AutentisertOkModell) => void, vedAutentiseringFeilet: (respons: l.AutentiseringResponsModell) => void): IPromise<void> {
        return this.autentiseringsRes.autentiserMedTotpReservekode(spesifikasjon).$promise
            .then(respons => {
                if (respons.status !== l.AutentiseringStatus.autentisert) {
                    vedAutentiseringFeilet(respons);
                    return;
                }
                this.brukerAutentisertMotServer(respons.autentisertBruker);
                vedAutentiseringOk(respons.autentisertBruker);
            })
            //ved ikke-200 respons
            .catch(e => vedAutentiseringFeilet(this.requstErrorSomAutentiseringResponsModell(e)));
    }

    /**
     * Autentiserer bruker mot server med sms otp reservekode.
     * Oppdaterer kontekst med brukerdata returnert av server,
     * fyrer brukerLoggetInnEvent.
     *
     * Resultatet av autentiseringen håndteres via callbackmetodene.
     */
    autentiserMedSmsOtpReservekode(spesifikasjon: l.SmsOtpReservekodeInnloggingsSpesifikasjon, vedAutentiseringOk: (autentisert: l.AutentisertOkModell) => void, vedAutentiseringFeilet: (respons: l.AutentiseringResponsModell) => void): IPromise<void> {
        return this.autentiseringsRes.autentiserMedSmsOtpReservekode(spesifikasjon).$promise
            .then(respons => {
                if (respons.status !== l.AutentiseringStatus.autentisert) {
                    vedAutentiseringFeilet(respons);
                    return;
                }
                this.brukerAutentisertMotServer(respons.autentisertBruker);
                vedAutentiseringOk(respons.autentisertBruker);
            })
            //ved ikke-200 respons
            .catch(e => vedAutentiseringFeilet(this.requstErrorSomAutentiseringResponsModell(e)));
    }

    /**
     * Autentiserer bruker mot criipto med openid.
     * Oppdaterer kontekst med brukerdata returnert av server,
     * fyrer brukerLoggetInnEvent.
     *
     * Feilet autentisering må håndteres via IPromise.catch callback.
     * Kaller er ansvarlig for å identifisere nødvendig binding av openid subject ved å
     * inspisere respons {@link l.AutentiseringResponsModell} argumentet gitt til IPromise.catch callback.
     */
    /*
    kan ikke lenger gjøre autentisering her - må delegere til kaller for å håndtere mulig styreplan-bruker
    autentiserMotCriipto(spesifikasjon: l.CriiptoTokenInnloggingsSpesifikasjon): IPromise<l.AutentisertOkModell> {
        const defered = this.$q.defer<l.AutentisertOkModell>();
        this.criiptoOIDCRessurs.autentiser(spesifikasjon).$promise
            .then((respons) => {
                if (respons.status !== l.AutentiseringStatus.autentisert) {
                    debug('openid autentisering feilet', respons);
                    defered.reject(respons);
                    return;
                }

                debug('openid autentisert - bruker funnet', respons);
                this.brukerAutentisertMotServer(respons.autentisertBruker);
                defered.resolve(respons.autentisertBruker)
            })
            //ved ikke-200 respons
            .catch(e => defered.reject(this.requstErrorSomAutentiseringResponsModell(e)));

        return defered.promise;
    }
  */

    //merk at bindAutentisertBruker svartelister det aktive tokenet
    bindAutentisertBrukerTilCriiptoSubject(spesifikasjon:l.CriiptoBindBrukerSpesifikasjon): IPromise<l.AutentisertOkModell> {
        return this.håndterBindCriiptoRespons(this.criiptoOIDCRessurs.bindAutentisertBruker(spesifikasjon))
    }

    bindIkkeAutentisertBrukerTilCriiptoSubjectFraResettoken(spesifikasjon:l.CriiptoBindFraTokenSpesifikasjon): IPromise<l.AutentisertOkModell> {
        return this.håndterBindCriiptoRespons(this.criiptoOIDCRessurs.bindIkkeAutentisertBrukerFraToken(spesifikasjon))
    }

    håndterBindCriiptoRespons(autentiseringRespons: l.AutentiseringResponsModell) {
        const defered = this.$q.defer<l.AutentisertOkModell>();

        autentiseringRespons.$promise.then(respons => {
            if (respons.status !== l.AutentiseringStatus.autentisert) {
                debug('openid bindBrukerTilCriiptoSubject feilet', respons);
                defered.reject(respons);
                return;
            }

            debug('bindAutentisertBruker returnert', respons);
            this.lokalLoggUt();
            this.brukerAutentisertMotServer(respons.autentisertBruker);
            defered.resolve(respons.autentisertBruker);
        })
          //ved ikke-200 respons
          .catch(e => defered.reject(this.requstErrorSomAutentiseringResponsModell(e)));

        return defered.promise;
    }

    /**
     * Laster AutentisertOkModel fra lokalt lager,
     * dersom slik finnes initialiseres kontekst basert på denne og token valideres mot server
     * hvorpå kontekst oppdateres og {@link #tidligererInnloggetBrukerLastetEvent} fyres.
     * Dersom tokenverifisering mot server feilet vil server svare med 401 - dette bør
     * håndteres via lytting til httpUnauthorizedEvent event som bør vise innloggingssiden.
     */
    initierKontekstFraLokaltTokenOgValider() {
        return this.lokallagretAutentisertModel.hent().then(autentisert => {
            if (autentisert == null) {
                debug("initierKontekstFraLokaltTokenOgValider: ingen bruker å laste fra lokalt lager");
                return;
            }
            debug("initierKontekstFraLokaltTokenOgValider: laster bruker fra lokalt lager", autentisert);

            this.brukerLastetFraLokallagretModell(autentisert);

            //valider token mot server - fører til httpUnauthorizedEvent dersom token ikke gyldig
            return this.autentiseringsRes.hentKontekst().$promise
              .then(autentisert =>
                  this.tidligererInnloggetBrukerValidertMotServer(autentisert));
        });
    }

    /**
     * Starter timer som automatisk fornyer aktivt kort token
     * {@link Konfigurasjon#kortTokenFornymargin} tid før dets utløp.
     *
     * Merk at vi ikke selv beregner hvor lenge det er til tokenet utløper
     * men leser dette fra serverrespons.
     *
     * Ment for fornying av token med type Tokenvarighet.kort
     */
    startAutomatiskKortTokenfornyelse(){
        //sørger for at vi ikke er sårbare for multiple kall til start
        this.stoppAutomatiskKortTokenfornyelse();

        this.automatiskTokenfornyelseTimer = this.$interval(
            () => this.validerKortTokenOgFornyDersomUtløperSnart(),
            1000*60); //valider token en gang per minutt
    }

    private validerKortTokenOgFornyDersomUtløperSnart(){
        this.autentiseringsRes.validerToken().$promise
            .then(tokeninfo => {
                if (!tokeninfo.erGyldig){
                    this.lokalLoggUt();
                    this.$rootScope.$broadcast(Autentisering.tokenValideringFeiletEvent, tokeninfo);
                    return;
                }

                if (tokeninfo.tokeninfo.tokenvarighet !== l.Tokenvarighet.kort){
                    this.stoppAutomatiskKortTokenfornyelse();
                    return;
                }

                const tidTilUtløper = moment.duration(tokeninfo.tokeninfo.tidTilUtloeper);
                const tidTilForny = tidTilUtløper.clone().subtract(this.konfigurasjon.kortTokenFornymargin);
                debug(`validerKortTokenOgFornyDersomUtløperSnart: fornyer token om ${tidTilForny.humanize()} (${Math.floor(tidTilForny.asSeconds())} s), utløper om ${tidTilUtløper.humanize()}`);
                if (tidTilForny.asSeconds() < 0) this.fornyKortToken();
            })
    }

    /** Stopper timeren som automatisk fornyer token før utløp */
    stoppAutomatiskKortTokenfornyelse() {
        if (!this.automatiskTokenfornyelseTimer) return;
        this.$interval.cancel(this.automatiskTokenfornyelseTimer);
    }

    /** Oppgraderer til medium token, stopper automatisk fornyelse av kort token */
    oppgraderTilMediumToken(): IPromise<l.AutentisertOkModell> {
        debug('oppgraderTilMediumToken')
        this.stoppAutomatiskKortTokenfornyelse(); //sørger for at vi ikke lenger periodisk fornyer token
        const resultat = this.fornyToken(l.Tokenvarighet.medium);
        resultat.then(()=>this.broadcastBrukerAutentisertEvent())
        return resultat;
    }

    /**
     * Fornyer aktivt langt token
     * dersom det er mindre enn {@link Konfigurasjon#langTokenFornymargin} tid til tokenet utløper
     *
     * Merk at vi ikke selv beregner hvor lenge det er til tokenet utløper
     * (noe som ville vært sårbart avvvik mellom klientens klokke og serverens klokke)
     * men leser dette fra AutentisertOkModell returnert ved innlogging/tokenvalidering.
     * Så fremt startAutomatiskKortTokenfornyelse() blir kalt direkte etter innlogging/tokenvalidering
     * vil dette være ok.
     *
     * Ment for fornying av token med type Tokenvarighet.lang
     */
    fornyLangTokenDersomUtløperSnart(): IPromise<l.AutentisertOkModell> {
        const tidTilUtløper = this.kontekst.aktivtTokenTidTilUtløper();
        const tidTilForny = tidTilUtløper.clone().subtract(this.konfigurasjon.langTokenFornymargin);

        if (tidTilForny.asMilliseconds() > 0) {
            debug(`fornyLangTokenDersomUtløperSnart: avbrutt da tidTilForny (${tidTilForny.humanize()}) > 0`);
            return this.$q.reject();
        }
        debug(`fornyLangTokenDersomUtløperSnart: fornyer token, utløper om ${tidTilUtløper.humanize()} (${tidTilUtløper.asMilliseconds()} ms)`);
        return this.fornyToken(this.kontekst.aktivtTokenInfo().tokenvarighet);
    }

    /**
     * Logger ut bruker og kaller angitt 'gåTilInnloggingssiden' param dersom jwt token snart utløper.
     *
     * Merk at server ved innlogging forteller oss hvor lenge det er til token utløper
     * {@link JwtTokenInfoModell#tidTilUtloeper}, vi er derfor ikke sårbare for
     * klokkeforskjell mellom klient og server.
     *
     * Ment for håndtering av token med type Tokenvarighet.medium
     *
     * Vi opererer med to frister - en bør benyttes når bruker autentiseres (fristType=fristVedAutentisering)
     * og en som bør benyttes ved navigasjon (fristType=fristVedNavigasjon).
     */
    reautentiserDersomTokenUtløperSnart(fristType: 'fristVedAutentisering' | 'fristVedNavigasjon', gåTilInnloggingssiden: () => void): boolean {
        if (!this.kontekst.erAutentisert()) return false;

        const margin = fristType === 'fristVedAutentisering' ? this.konfigurasjon.mediumTokenLoggutmarginVedAutentisering : this.konfigurasjon.mediumTokenLoggutmarginVedNavigasjon;
        const tidTilUtløper = this.kontekst.aktivtTokenTidTilUtløper();
        const tidTilLoggut = tidTilUtløper.clone().subtract(margin);
        debug(`reautentiserDersomTokenUtløperSnart: terskel for invalidering av token(type medium, ${fristType}) nådd om ${tidTilLoggut.humanize()}, utløper om ${tidTilUtløper.humanize()}`, tidTilUtløper.asMinutes(), tidTilLoggut.asMinutes());

        if (tidTilLoggut.asMinutes() > 1) return false;

        debug(`reautentiserDersomTokenUtløperSnart: terskel for invalidering av token(type medium, ${fristType}) nådd, logger ut bruker`);
        //ideelt sett burde vi her kalt Utlogging.loggUt() men dette er er web-spesifikk tjeneste...
        this.loggUt();
        //vi venter ikke med å redirigere til innloggingssiden til logut-kallet har returnert, vi ønsker å avbryte eventuelle pågående operasjoner så raskt som mulig
        //og antar at logut-kallet returnerer uten feil
        gåTilInnloggingssiden();
        return true;
    }

    /**
     * Logger ut bruker og informerer server om utlogging.
     * @see lokalLoggUt()
     */
    loggUt(): IPromise<void> {
        //sørg for at lokale interessenter kan reagere på at bruker logger ut
        this.lokalLoggUt();
        //gjør kall mot server som logger ut
        return this.autentiseringsRes.loggut().$promise;
    }

    /**
     * Logger ut bruker lokalt.
     * Fyrer {@link #brukerLoggetUtEvent} lokalt.
     * Fyrer {@link #brukerLoggetUtEvent} over InterWindowBus dersom interwindowbusBroadcastLogutEvent=true.
     * Motsatsen til {@link #initierAutentisertBruker()}
     *
     * - nullstiller {@link Kontekst}
     * - fjerner 'autentisert' modell fra lokalt lager
     */
    lokalLoggUt(interwindowbusBroadcastLogutEvent = true){
        debug('lokalLoggUt: sletter lokallagret autmodell');
        this.lokallagretAutentisertModel.slett();
        this.kontekst.nullstill();
        this.broadcastBrukerLoggetUt(interwindowbusBroadcastLogutEvent);
    }

    /**
     * Logger ut bruker fra angitt enhet ved å informerer server om at angitt token skal invalideres.
     */
    loggUtEnhet(tokenId:string){
        return this.autentiseringsRes.loggutEnhet(tokenId).$promise
          .then(() => {
              this.meldingTilBruker.infomelding(
                ///tekst som vises nå enkeltenhet blir logget ut
                this.gettextCatalog.getString('Du er nå logget ut fra enheten')
              );
          })
    }

    /**
     * Logger ut bruker samt informerer server om at alle token for aktiv bruker skal invalideres.
     */
    loggUtAlleEnheter(){
        return this.autentiseringsRes.loggutAlleEnheter().$promise
          .then(() => {
              this.lokalLoggUt();
          })
    }

    autentiseringsmetodenavn(metode: l.Autentiseringsmetode) {
        switch (metode) {
            case l.Autentiseringsmetode.passord:
                return this.gettextCatalog.getString('Passord og brukernavn');
            case l.Autentiseringsmetode.totpOtp:
                return this.gettextCatalog.getString('Passord og brukernavn med engangskode fra kodegenerator');
            case l.Autentiseringsmetode.smsOtp:
                return this.gettextCatalog.getString('Passord og brukernavn med engangskode via sms');
            case l.Autentiseringsmetode.criiptoOpenId:
                return this.gettextCatalog.getString('BankID');
            default:
                return l.Autentiseringsmetode[metode]
        }
    }

    autentiseringsmetodenavnKort(metode: l.Autentiseringsmetode) {
        switch (metode) {
            case l.Autentiseringsmetode.passord:
                return this.gettextCatalog.getString('Passord');
            case l.Autentiseringsmetode.totpOtp:
                return this.gettextCatalog.getString('Kodegenerator');
            case l.Autentiseringsmetode.smsOtp:
                return this.gettextCatalog.getString('Sms');
            case l.Autentiseringsmetode.criiptoOpenId:
                return this.gettextCatalog.getString('E-Id');
            default:
                return l.Autentiseringsmetode[metode]
        }
    }

    autentiseringsmetodernavn(metoder: l.Autentiseringsmetode[]) {
        return metoder.map(metode => this.autentiseringsmetodenavn(metode))
    }

    autentiseringsmetodernavnKort(metoder: l.Autentiseringsmetode[]) {
        return metoder.map(metode => this.autentiseringsmetodenavnKort(metode))
    }



    //hjelpemetoder

    private requstErrorSomAutentiseringResponsModell(error:any): l.AutentiseringResponsModell{
        const respons: l.AutentiseringResponsModell = {status: l.AutentiseringStatus.ugyldigSpesifikasjon}
        if(erIkkeNill(error) && erIkkeNill(error.status) && erIkkeNill(error.statusText)) {
            respons.error = {kode: error.status, melding: error.statusText};
        }
        return respons;
    }

    /**
     * Populerer kontekst med autentisert bruker hentet fra lokalt datalager.
     *
     * @see initierAutentisertBruker
     */
    private brukerLastetFraLokallagretModell(autentisert: l.AutentisertOkModell){
        this.initierAutentisertBruker(autentisert);
    }

    /**
     * Populerer kontekst med autentisert bruker returnert fra server,
     * fyrer {@link #brukerLoggetInnEvent} og {@link #brukerAutentisertEvent}.
     *
     * @see initierAutentisertBruker
     */
    public brukerAutentisertMotServer(autentisert: l.AutentisertOkModell) {
        debug("brukerAutentisertMotServer %O", autentisert);
        this.initierAutentisertBruker(autentisert);
        this.broadcastBrukerLoggetInnEvent();
        this.broadcastBrukerAutentisertEvent();
    }

    /**
     * Populerer kontekst med validert bruker returnert fra server,
     * fyrer {@link #tidligererInnloggetBrukerLastetEvent} og {@link #brukerAutentisertEvent}.
     *
     * @see initierAutentisertBruker
     */
    private tidligererInnloggetBrukerValidertMotServer(autentisert: l.AutentisertOkModell) {
        debug("tidligererInnloggetBrukerValidertMotServer %O", autentisert);
        this.initierAutentisertBruker(autentisert);
        this.broadcastTidligererInnloggetBrukerLastetEvent();
        this.broadcastBrukerAutentisertEvent();
    }

    /**
     * Populerer kontekst med autentisert bruker, sørger for at token blir inkludert i ressurskall.
     *
     * - populerer {@link Kontekst}
     * - lagrer 'autentisert' modell i lokalt lager

     *
     * Merk at tjenesten {@link AuthorizationHeader} er ansvarlig for å sette Authorazation header på kall gjort via
     * angulars $http tjeneste.
     */
    private initierAutentisertBruker(autentisert: l.AutentisertOkModell) {
        debug("initierAutentisertBruker %O", autentisert);
        this.kontekst.populerKontekst(autentisert);
        this.lokallagretAutentisertModel.lagre(autentisert);
    }

    private fornyToken(tokenvarighet: l.Tokenvarighet): IPromise<l.AutentisertOkModell> {
        debug('fornyToken', tokenvarighet)
        const respons = this.$q.defer<l.AutentisertOkModell>();
        this.autentiseringsRes.fornyTokenV2(tokenvarighet).$promise
            .then(autentisert => {
                this.initierAutentisertBruker(autentisert);
                respons.resolve(autentisert);
            })
            .catch(e => {
                debug('fornyToken feilet, logger ut', e);
                this.loggUt()
                    .finally(()=> respons.reject('fornyToken feilet'))
            });

        return respons.promise;
    }

    /**
     * Gjennomfører fornyelse av kort token.
     * Tar hensyn til at multiple arkfaner kan forsøke å gjøre dette samtidig.
     */
    private fornyKortToken() {
        //utsett dersom annet vindu har startet prosessen
        if (this.fornyKortTokenInitiertIAnnenFane) return;

        //signaliser til andre arkfaner at vi oppdaterer token
        this.interWindowBus.signaliser(Autentisering.fornyKortTokenEvent, 'start');

        this.fornyToken(l.Tokenvarighet.kort)
            .then(autentisert => {
                //sørg for at det nye tokenet blir automatisk fornyet
                this.startAutomatiskKortTokenfornyelse();
            })
            //signaliser til andre arkfaner at token er oppdatert
            .finally(()=> this.interWindowBus.signaliser(Autentisering.fornyKortTokenEvent, 'ferdig'));
    }

    private lyttTilFornyKortTokenHendelseIAnnenFane() {
        this.interWindowBus.ved(Autentisering.fornyKortTokenEvent, status => {
            debug("lyttTilFornyKortTokenHendelseIAnnenFane", status);

            //sjekk om token har blitt oppdatert av annen part
            if (status === 'start') this.håndterFornyKortTokenStartHendelse();
            if (status === 'ferdig') this.håndterFornyKortTokenFerdigHendelse();
        });
    }

    /** Kalles når annet vindu starter prosessen med å fornye token. */
    private håndterFornyKortTokenStartHendelse(){
        this.fornyKortTokenInitiertIAnnenFane = true;

        //start timer som sikrer at håndterFornyKortTokenFerdigHendelse() blir kalt
        this.fornyKortTokenHendelseTimeout = this.$timeout(()=>this.håndterFornyKortTokenFerdigHendelse(), 1000*10);//sørg for at vi signaliserer ferdig etter 10 sekunder
    }

    /** Kalles når annet vindu har fornyet token.
     * Sørger for å populere kontekst fra lokallagret autentiseringsmodell
     * (som har blitt oppdatert av vindu som fornyet tokenet) */
    private håndterFornyKortTokenFerdigHendelse(){
        this.$timeout.cancel(this.fornyKortTokenHendelseTimeout);

        this.lokallagretAutentisertModel.hent()
            .then(autentisert =>{
                this.initierAutentisertBruker(autentisert);
            })
            .finally(()=>{
                this.fornyKortTokenInitiertIAnnenFane = false;
            })
    }

    private broadcastTidligererInnloggetBrukerLastetEvent() {
        debug('trigger tidligererInnloggetBrukerLastetEvent event');
        this.$rootScope.$broadcast(Autentisering.tidligererInnloggetBrukerLastetEvent, this.kontekst.autentisertBruker());
    }

    private broadcastBrukerAutentisertEvent() {
        debug('trigger brukerAutentisertEvent event');
        this.$rootScope.$broadcast(Autentisering.brukerAutentisertEvent, this.kontekst.autentisertBruker());
    }

    private broadcastBrukerLoggetInnEvent() {
        debug('trigger brukerLoggetInnEvent event');
        this.$rootScope.$broadcast(Autentisering.brukerLoggetInnEvent, this.kontekst.autentisertBruker());
    }

    private broadcastBrukerLoggetUt(interwindowbusBroadcastLogutEvent = true) {
        debug('trigger brukerLoggetUtEvent event');
        this.$rootScope.$broadcast(Autentisering.brukerLoggetUtEvent);
        if (interwindowbusBroadcastLogutEvent) this.interWindowBus.signaliser(Autentisering.brukerLoggetUtEvent);
    }

}
